import { isWeb } from "tamagui";

/**
 * How long to wait before reporting that an OS picker never answered. The
 * timeout alone is ambiguous: the expo web shims may never report a
 * cancellation, so a backed-out dialog and a dialog the browser refused to
 * open both leave the promise pending. `hadUserActivation` is what splits
 * them.
 */
export const PICKER_NO_RESPONSE_TIMEOUT_MS = 60_000;

export type PickerNoResponseInfo = {
  /**
   * Whether the browser reported transient user activation when the picker
   * opened. The expo shims open the file dialog with a synthetic click, which
   * browsers reject without activation — so `false` means the dialog almost
   * certainly never opened (the stuck-spinner hang), while `true` or
   * `undefined` (browser doesn't implement `navigator.userActivation`) leaves
   * the timeout ambiguous between a hang and an unreported cancel.
   */
  hadUserActivation: boolean | undefined;
};

/**
 * Arms the no-response timer only where the hang can happen. Native pickers
 * always answer, and browsing an OS picker for over a minute is ordinary there
 * — arming on native would emit a spurious NO_RESPONSE ahead of the real
 * terminal and force last-terminal-wins dedupe onto every native query.
 */
export const startPickerNoResponseTimer = (
  onNoResponse: ((info: PickerNoResponseInfo) => void) | undefined,
): (() => void) => {
  if (!isWeb || !onNoResponse) {
    return () => {};
  }
  const hadUserActivation =
    typeof navigator === "undefined"
      ? undefined
      : navigator.userActivation?.isActive;
  const timer = setTimeout(
    () => onNoResponse({ hadUserActivation }),
    PICKER_NO_RESPONSE_TIMEOUT_MS,
  );
  return () => clearTimeout(timer);
};
